import { publicApiClient } from '~/lib/api/client';

export type MetaBase = {
  id: string;
  name: string;
  code?: string | null;
};

export type MetaDistrict = MetaBase & {
  regional_code?: string | null;
};

export type MetaRegionalOffice = {
  code: string;
  districts: string[];
};
export type MetaAgaDivision = MetaBase & {
  districtId?: string | null;
  teaRegionId?: string | null;
};
export type MetaGramasevaDivision = MetaBase & {
  agaDivisionId?: string | null;
  no?: string | number | null;
};

export type MetaPagedResult<T> = {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
};
export type MetaAreaUnit = MetaBase;
export type MetaLandOwnership = MetaBase;
export type MetaMonsoonSeason = MetaBase & { description?: string | null };
export type MetaMonsoonSeasonRef = Pick<MetaMonsoonSeason, 'id' | 'name'>;
export type MetaSourceCutting = MetaBase;
export type MetaTeaGrowingRegionRef = Pick<MetaTeaGrowingRegion, 'id' | 'name'>;

export type MetaTriCultivar = MetaBase & {
  description?: string | null;
  teaGrowingRegions?: MetaTeaGrowingRegionRef[];
};

export type TriCultivarsAllParams = {
  page?: number;
  pageSize?: number;
  search?: string;
  teaRegionId?: string;
};
export type MetaNurseryExperience = MetaBase;
export type MetaTeaGrowingRegion = MetaBase & { description?: string | null };
export type MetaTiRange = {
  id: string;
  divName: string;
  regCode: number;
};

export async function getLandOwnerships(): Promise<MetaLandOwnership[]> {
  const res = await publicApiClient.get('/meta/land-ownerships');
  return res.data;
}

function normalizeItemsArray<T>(raw: unknown): T[] {
  if (Array.isArray(raw)) return raw as T[];
  if (raw && typeof raw === 'object' && Array.isArray((raw as { items?: unknown }).items)) {
    return (raw as { items: T[] }).items;
  }
  return [];
}

export async function getDistricts(): Promise<MetaDistrict[]> {
  const res = await publicApiClient.get('/meta/districts');
  return normalizeItemsArray<MetaDistrict>(res.data);
}

export async function getRegionalOffices(): Promise<MetaRegionalOffice[]> {
  const res = await publicApiClient.get('/meta/regional-offices');
  return normalizeItemsArray<MetaRegionalOffice>(res.data);
}

const META_PAGE_SIZE_MAX = 100;
const META_PAGE_FETCH_CAP = 500;

function normalizePaged<T>(raw: unknown): MetaPagedResult<T> {
  if (!raw || typeof raw !== 'object') {
    return { items: [], total: 0, page: 1, pageSize: 0, totalPages: 0, hasNextPage: false };
  }
  const o = raw as Record<string, unknown>;
  const items = Array.isArray(o.items) ? (o.items as T[]) : [];
  return {
    items,
    total: Number(o.total) || 0,
    page: Number(o.page) || 1,
    pageSize: Number(o.pageSize) || 0,
    totalPages: Number(o.totalPages) || 0,
    hasNextPage: Boolean(o.hasNextPage),
  };
}

async function getAgaDivisionsPage(
  districtId: string,
  page: number,
  pageSize: number
): Promise<MetaPagedResult<MetaAgaDivision>> {
  const res = await publicApiClient.get('/meta/aga-divisions', {
    params: { districtId, page, pageSize },
  });
  return normalizePaged<MetaAgaDivision>(res.data);
}

async function getGramasevaDivisionsPage(
  agaDivisionId: string,
  page: number,
  pageSize: number
): Promise<MetaPagedResult<MetaGramasevaDivision>> {
  const res = await publicApiClient.get('/meta/gramaseva-divisions', {
    params: { agaDivisionId, page, pageSize },
  });
  return normalizePaged<MetaGramasevaDivision>(res.data);
}

export async function getAgaDivisions(districtId: string): Promise<MetaAgaDivision[]> {
  const id = districtId.trim();
  if (!id) return [];

  const all: MetaAgaDivision[] = [];
  let totalPages = 0;
  let page = 1;

  while (page <= META_PAGE_FETCH_CAP) {
    const chunk = await getAgaDivisionsPage(id, page, META_PAGE_SIZE_MAX);
    all.push(...chunk.items);

    if (page === 1 && chunk.totalPages > 0) {
      totalPages = chunk.totalPages;
    }

    if (totalPages > 0) {
      if (page >= totalPages) break;
    } else if (!chunk.hasNextPage) {
      break;
    }

    page++;
    if (!chunk.items.length && page > 1) break;
  }

  return all;
}

export async function getGramasevaDivisions(
  agaDivisionId: string
): Promise<MetaGramasevaDivision[]> {
  const id = agaDivisionId.trim();
  if (!id) return [];

  const all: MetaGramasevaDivision[] = [];
  let totalPages = 0;
  let page = 1;

  while (page <= META_PAGE_FETCH_CAP) {
    const chunk = await getGramasevaDivisionsPage(id, page, META_PAGE_SIZE_MAX);
    all.push(...chunk.items);

    if (page === 1 && chunk.totalPages > 0) {
      totalPages = chunk.totalPages;
    }

    if (totalPages > 0) {
      if (page >= totalPages) break;
    } else if (!chunk.hasNextPage) {
      break;
    }

    page++;
    if (!chunk.items.length && page > 1) break;
  }

  return all;
}

export async function getTeaGrowingRegionsByAgaDivision(
  agaDivisionId: string
): Promise<MetaTeaGrowingRegion | MetaTeaGrowingRegion[]> {
  const res = await publicApiClient.get('/meta/tea-growing-regions/by-aga-division', {
    params: { agaDivisionId },
  });
  return res.data;
}

export async function getMonsoonSeasons(): Promise<MetaMonsoonSeason[]> {
  const res = await publicApiClient.get('/meta/monsoon-seasons');
  return res.data;
}

export async function getMonsoonSeasonsByAgaDivision(
  agaDivisionId: string
): Promise<MetaMonsoonSeasonRef[]> {
  const id = agaDivisionId.trim();
  if (!id) return [];
  const res = await publicApiClient.get('/meta/monsoon-seasons/by-aga-division', {
    params: { agaDivisionId: id },
  });
  return res.data ?? [];
}

export async function getAreaUnits(): Promise<MetaAreaUnit[]> {
  const res = await publicApiClient.get('/meta/area-units');
  return res.data;
}

export async function getSourceCuttings(): Promise<MetaSourceCutting[]> {
  const res = await publicApiClient.get('/meta/source-cuttings');
  return res.data;
}

export async function getTriCultivarsByTeaRegion(teaRegionId: string): Promise<MetaTriCultivar[]> {
  const id = teaRegionId.trim();
  if (!id) return [];
  const res = await publicApiClient.get('/meta/tri-cultivars', { params: { teaRegionId: id } });
  return res.data;
}

async function getTeaGrowingRegionsPage(
  page: number,
  pageSize: number
): Promise<MetaPagedResult<MetaTeaGrowingRegion>> {
  const res = await publicApiClient.get('/meta/tea-growing-regions', {
    params: { page, pageSize },
  });
  return normalizePaged<MetaTeaGrowingRegion>(res.data);
}

/** Fetches all tea growing regions (paginated API, aggregated). */
export async function getTeaGrowingRegions(): Promise<MetaTeaGrowingRegion[]> {
  const all: MetaTeaGrowingRegion[] = [];
  let totalPages = 0;
  let page = 1;

  while (page <= META_PAGE_FETCH_CAP) {
    const chunk = await getTeaGrowingRegionsPage(page, META_PAGE_SIZE_MAX);
    all.push(...chunk.items);

    if (page === 1 && chunk.totalPages > 0) {
      totalPages = chunk.totalPages;
    }

    if (totalPages > 0) {
      if (page >= totalPages) break;
    } else if (!chunk.hasNextPage) {
      break;
    }

    page++;
    if (!chunk.items.length && page > 1) break;
  }

  if (all.length) return all;

  const res = await publicApiClient.get('/meta/tea-growing-regions');
  const data = res.data;
  if (Array.isArray(data)) return data as MetaTeaGrowingRegion[];
  return normalizePaged<MetaTeaGrowingRegion>(data).items;
}

export async function getTriCultivarsAll(
  params: TriCultivarsAllParams = {}
): Promise<MetaPagedResult<MetaTriCultivar>> {
  const res = await publicApiClient.get('/meta/tri-cultivars/all', {
    params: {
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 50,
      ...(params.search?.trim() ? { search: params.search.trim() } : {}),
      ...(params.teaRegionId?.trim() ? { teaRegionId: params.teaRegionId.trim() } : {}),
    },
  });
  return normalizePaged<MetaTriCultivar>(res.data);
}

/** Fetches all TRI cultivars (paginated API, aggregated). */
export async function getAllTriCultivars(search?: string): Promise<MetaTriCultivar[]> {
  const all: MetaTriCultivar[] = [];
  let totalPages = 0;
  let page = 1;

  while (page <= META_PAGE_FETCH_CAP) {
    const chunk = await getTriCultivarsAll({
      page,
      pageSize: META_PAGE_SIZE_MAX,
      search,
    });
    all.push(...chunk.items);

    if (page === 1 && chunk.totalPages > 0) {
      totalPages = chunk.totalPages;
    }

    if (totalPages > 0) {
      if (page >= totalPages) break;
    } else if (!chunk.hasNextPage) {
      break;
    }

    page++;
    if (!chunk.items.length && page > 1) break;
  }

  return all;
}

export async function getNurseryExperiences(): Promise<MetaNurseryExperience[]> {
  const res = await publicApiClient.get('/meta/nursery-experiences');
  return res.data;
}

export async function getTiRanges(districtId: string): Promise<MetaTiRange[]> {
  const id = districtId.trim();
  if (!id) return [];
  const res = await publicApiClient.get('/meta/ti-ranges', { params: { districtId: id } });
  return res.data;
}
